import { useState, useMemo, useEffect } from 'react';

/**
 * Client-side pagination over an already-filtered list.
 *
 * @param {Array}  items         the full (filtered) list
 * @param {object} options
 * @param {number} options.initialPageSize
 * @param {*}      options.resetKey  change this to jump back to page 1 — pass the
 *                                   active filters, so narrowing the list does not
 *                                   strand the user on a page that no longer exists
 */
export const usePagination = (items = [], { initialPageSize = 25, resetKey = null } = {}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Back to the first page whenever the filters change.
  useEffect(() => { setPage(1); }, [resetKey, pageSize]);

  // The list can also shrink underneath us (a record is deleted, data reloads).
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  return {
    pageItems,
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    total,
    // 1-based inclusive range for "Showing X to Y of Z"
    rangeStart: total === 0 ? 0 : (safePage - 1) * pageSize + 1,
    rangeEnd: Math.min(safePage * pageSize, total),
  };
};

export default usePagination;
